import {isEmpty, isFinite, isNumber, pickBy} from 'lodash-es';
import {useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {ValidationError} from 'yup';

import {
  AfriexAdminPermission,
  AfriexTransactionFeeType,
} from '@afriex/core/domain/enums';

import {Button, Header} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import Visibility from '@/components/Visibility';
import {feeService} from '@/services/feeService';
import type {TransactionFee} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';
import {isolateError} from '@/utils/isolateError';
import {logger} from '@/utils/logger';

import type {FeeEntryRef} from './FeeEntry';
import FeeEntry from './FeeEntry';
import FeeOverrideSection from './FeeOverrides';
import {baseFeeSchema} from './validation';

const Fees = () => {
  const [fees, setFees] = useState<{
    baseFee?: TransactionFee;
    overrides: TransactionFee[];
  }>({overrides: []}); // fees from API

  const baseEntryRef = useRef<FeeEntryRef>(null);
  const [submitting, setSubmitting] = useState(false);
  const updateFees = async () => {
    const baseFee = validateBaseFee(baseEntryRef.current?.getFee() ?? {});
    if (!baseFee) {
      return;
    }
    setSubmitting(true);

    const feeToUpdate = {
      // ensure id is preserved, diffing will be done on the BE
      baseFee: {...(fees.baseFee ?? {}), ...baseFee},
      overrideFees: fees.overrides,
    };
    const [_, error] = await isolateError(
      feeService.updateTransactionFees(feeToUpdate),
    );

    if (!error) {
      const [updatedFees, _] = await isolateError(
        feeService.getTransactionFees(),
      );
      setFees({
        baseFee: updatedFees?.baseFee,
        overrides: updatedFees?.overrideFees ?? [],
      });
    }

    setSubmitting(false);
    if (error) {
      toast.error(getResponseMessage(error) || 'Error updating fees');
      return;
    }
    toast.success('Fees updated');
  };

  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );

  const validateBaseFee = (values: Partial<TransactionFee>) => {
    const {baseValue, percentValue, maxFeeValue, type} = values;
    const baseFee: Pick<
      TransactionFee,
      'baseValue' | 'percentValue' | 'maxFeeValue' | 'type'
    > = {
      baseValue: isNumber(baseValue) ? Number(baseValue) : undefined,
      percentValue: isNumber(percentValue) ? Number(percentValue) : undefined,
      maxFeeValue: isNumber(maxFeeValue) ? Number(maxFeeValue) : undefined,
      type: type || AfriexTransactionFeeType.DEFAULT,
    };

    try {
      setValidationError(undefined);
      const cleanBaseFee = pickBy(
        baseFee,
        (value) => isFinite(value) || !isEmpty(value),
      );
      return baseFeeSchema.validateSync(cleanBaseFee) as TransactionFee;
    } catch (e) {
      if (e instanceof ValidationError) {
        setValidationError(e.message);
        return;
      }
      setValidationError('Unknown error');
    }
    return undefined;
  };

  const [loading, setLoading] = useState(false);

  const fetchFees = async () => {
    setLoading(true);
    const [data, error] = await isolateError(feeService.getTransactionFees());
    setLoading(false);
    if (error) {
      toast.error(getResponseMessage(error) || 'Error fetching fees');
      return;
    }

    return data;
  };

  useEffect(() => {
    fetchFees()
      .then((result) => {
        const {baseFee, overrideFees = []} = result ?? {};
        setFees({
          baseFee,
          overrides: overrideFees,
        });
      })
      .catch((error) => {
        logger.log(error);
      });
  }, []);

  const baseFee = fees?.baseFee ?? ({type: 'default'} as TransactionFee);

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title="Fee Management" />

      <section className="pt-5 md:pt-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row flex-wrap justify-between items-start sm:items-center gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              <h1 className="text-base sm:text-lg font-medium">Default Fee</h1>
              <p className="text-gray-500 text-sm">
                (Fixed fees are applied on the source currency)
              </p>
            </div>

            <PermissionsProvider
              permission={AfriexAdminPermission.TransactionFeeCreate}
            >
              <Button
                colorScheme="cyan"
                onClick={updateFees}
                isLoading={submitting}
              >
                Update Fees
              </Button>
            </PermissionsProvider>
          </div>

          <div className="">
            <FeeEntry
              key={`base-fee${baseFee.id}`}
              editStyle="inline"
              innerRef={baseEntryRef}
              type="default"
              fee={baseFee}
            />
          </div>

          <div>
            {validationError && (
              <div className="text-red-500 text-sm">{validationError}</div>
            )}
          </div>
        </div>
      </section>

      <Visibility visible={loading && !fees.overrides.length}>
        <div className="mt-9 text-center justify-center">
          <h3 className="text-xl">Loading...</h3>
        </div>
      </Visibility>

      <Visibility visible={!loading || fees.overrides.length > 0}>
        <div className="">
          <FeeOverrideSection
            overrides={fees.overrides}
            onDelete={(_, index) => {
              setFees((prev) => ({
                ...prev,
                overrides: prev.overrides.filter((_, i) => i !== index),
              }));
            }}
            onSave={(override) => {
              setFees((prev) => ({
                ...prev,
                // check if override already exists and update index otherwise add
                overrides: isEmpty(override.id)
                  ? [override].concat(prev.overrides)
                  : prev.overrides.map((cur) => {
                      if (cur.id === override.id) {
                        // preserve no editable fields
                        return {...cur, ...override};
                      }
                      return cur;
                    }),
              }));
            }}
          />
        </div>
      </Visibility>
    </main>
  );
};

export default Fees;
